import type { PromptAuthor } from "../../turn-author-identity.js";
import type { StreamEntry } from "./history/types.js";
import type { AgentTimelineItem } from "./types.js";
import { echoIsAttachmentSuperset } from "./user-message-attachments.js";

type KeptPrompt = StreamEntry & { kind: "user_message" };

// A provider's copy lands seconds after its send; past this, same words are two prompts.
const ECHO_WINDOW_MS = 5 * 60_000;

type EchoSide = Pick<
  KeptPrompt,
  "promptAuthor" | "ts" | "messageId" | "clientMessageId" | "providerIds" | "hasProviderCopy"
>;

// Evidence `b` is its own prompt, not `a`'s copy: authors, times or provider ids differ.
export function echoIsDistinct(
  a: EchoSide,
  b: { promptAuthor?: PromptAuthor; ts?: number; messageId?: string },
  aIsSend: boolean,
  bIsSend = false,
): boolean {
  if (a.promptAuthor && b.promptAuthor && !sameAuthor(a.promptAuthor, b.promptAuthor)) return true;
  if (echoWindowPassed(a, b.ts)) return true;
  if (!bIsSend && copyAlreadyLanded(a, b.messageId)) return true;
  return !aIsSend && !bIsSend && differentMessageIds(a.messageId, b.messageId);
}

// An unsettled local send carries this device's clock, not the server's: no time test.
function echoWindowPassed(a: EchoSide, bTs: number | undefined): boolean {
  const at = a.clientMessageId === undefined ? a.ts : undefined;
  return at !== undefined && bTs !== undefined && Math.abs(at - bTs) > ECHO_WINDOW_MS;
}

// After a send's provider copy landed, a new provider id is another message (CYBORG-1172).
function copyAlreadyLanded(a: EchoSide, id: string | undefined): boolean {
  if (!a.hasProviderCopy || id === undefined || id === a.messageId) return false;
  return !(a.providerIds ?? []).includes(id);
}

function differentMessageIds(a: string | undefined, b: string | undefined): boolean {
  return a !== undefined && b !== undefined && a !== b;
}

// Provider ids a bubble holds once an id-less row folds in; after the ledger, it is its copy.
export function providerIdsAfter(
  row: StreamEntry & { kind: "user_message" },
  item: { messageId?: string; clientMessageId?: string },
  confirmed: boolean,
): { providerIds?: string[]; hasProviderCopy?: true } {
  const id = item.messageId;
  if (id === undefined || item.clientMessageId !== undefined || id === row.messageId) return {};
  const providerIds = [...new Set([...(row.providerIds ?? []), id])];
  return confirmed ? { providerIds, hasProviderCopy: true } : { providerIds };
}

export function foldedProviderIds(
  send: StreamEntry & { kind: "user_message" },
  copy: StreamEntry & { kind: "user_message" },
  confirmed: boolean,
): { providerIds?: string[]; hasProviderCopy?: true } {
  const ids = [...(copy.providerIds ?? []), ...(copy.messageId ? [copy.messageId] : [])];
  const fresh = ids.filter((id) => id !== send.messageId);
  if (fresh.length === 0) return {};
  const providerIds = [...new Set([...(send.providerIds ?? []), ...fresh])];
  return confirmed ? { providerIds, hasProviderCopy: true } : { providerIds };
}

export function sameAuthor(a: PromptAuthor, b: PromptAuthor): boolean {
  return a.kind === "human" && b.kind === "human"
    ? a.email.toLowerCase() === b.email.toLowerCase()
    : a.kind === "agent" && b.kind === "agent" && a.name === b.name;
}

export function settledTextOf(
  content: string,
  ts: number | undefined,
): { content: string; ts?: number } {
  return ts === undefined ? { content } : { content, ts };
}

// The bubble keeps its own words over the daemon's `[image]` rendering of them (CYBORG-1166).
export function settledWordsOf(row: StreamEntry & { kind: "user_message" }, text: string): string {
  return row.attachments?.length && echoIsAttachmentSuperset(row.content, text)
    ? row.content
    : text;
}

// Words settle a send only in flight, from a durable row (seq or provider id), word for word:
// a cybo row carries a client id OR a message id, never both.
export function wordsSettleThisSend(
  row: StreamEntry & { kind: "user_message" },
  item: Extract<AgentTimelineItem, { type: "user_message" }>,
  text: string,
  seq: number | undefined,
): boolean {
  if (row.clientMessageId === undefined || row.status !== "pending") return false;
  if (seq === undefined && item.messageId === undefined) return false;
  return (
    row.content === text ||
    (!!row.attachments?.length && echoIsAttachmentSuperset(row.content, text))
  );
}

// Repeat echoes a kept prompt already folded in: the page's copy of one is that prompt, not a new one.
export function seqsFoldedInto(retained: readonly StreamEntry[]): Set<number> {
  const out = new Set<number>();
  for (const e of retained)
    if (e.kind === "user_message") for (const seq of e.rows ?? []) out.add(seq);
  return out;
}
